import { CircleAlert, CircleHelp, X } from "lucide-react";
import { type ReactNode, useEffect } from "react";

// As peças pequenas do console. Poucas e de propósito: um console administrativo que ganha uma
// biblioteca de componentes antes de ter telas acaba com mais abstração do que uso.

export function cn(...partes: Array<string | false | null | undefined>): string {
  return partes.filter(Boolean).join(" ");
}

export function Botao({
  children,
  onClick,
  variante = "secundario",
  carregando,
  desabilitado,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  variante?: "primario" | "secundario" | "perigo";
  carregando?: boolean;
  desabilitado?: boolean;
  type?: "button" | "submit";
}) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50";
  const estilos = {
    primario: "bg-accent text-accent-foreground hover:bg-accent-hover",
    secundario: "bg-bg-tertiary text-text-primary border border-border hover:border-border-hover",
    perigo: "bg-transparent text-error border border-border hover:border-error",
  } as const;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={desabilitado || carregando}
      className={cn(base, estilos[variante])}
    >
      {carregando ? "…" : children}
    </button>
  );
}

export function Campo({
  rotulo,
  children,
  dica,
}: {
  rotulo: string;
  children: ReactNode;
  dica?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm text-text-secondary">{rotulo}</span>
      {children}
      {dica ? <span className="text-xs text-text-muted">{dica}</span> : null}
    </label>
  );
}

// Um `!` ou `?` que conta o resto quando o mouse passa por cima.
//
// 🔴 Abre no HOVER **e no FOCO**, e o foco nao e detalhe de acessibilidade: num celular nao existe
// passar o mouse, entao um aviso so-hover simplesmente NAO EXISTE la. Sendo um <button>, o toque da
// foco e o balao aparece — o mesmo que faz funcionar no teclado.
//
// ⚠️ `aria-label` carrega o texto inteiro porque o balao e visual: quem usa leitor de tela precisa
// ouvir o motivo, nao "icone".
export function Dica({
  texto,
  tom = "aviso",
}: {
  texto: string;
  tom?: "aviso" | "duvida";
}) {
  const Icone = tom === "aviso" ? CircleAlert : CircleHelp;
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        aria-label={texto}
        className={cn(
          "inline-flex rounded-full outline-none focus-visible:ring-1 focus-visible:ring-border-focus",
          tom === "aviso" ? "text-warning" : "text-text-muted",
        )}
      >
        <Icone className="h-4 w-4" aria-hidden="true" />
      </button>
      <span
        role="tooltip"
        // 🔴 O lado muda com a tela, e nao e capricho: centrado sobre o icone, metade do balao caia
        // FORA da tela no celular — o icone fica perto da borda esquerda la, e perto da direita no
        // desktop. Entao ele abre para a direita no estreito e para a esquerda no largo, que e
        // sempre o lado onde ha espaco.
        className="pointer-events-none absolute bottom-full left-0 z-20 mb-1.5 hidden whitespace-nowrap rounded-lg border border-border bg-bg-tertiary px-2 py-1 text-text-primary text-xs shadow-lg group-focus-within:block group-hover:block sm:left-auto sm:right-0"
      >
        {texto}
      </span>
    </span>
  );
}

export const classeEntrada =
  "rounded-lg border border-border bg-bg-primary px-3 py-2 text-sm text-text-primary placeholder:text-text-placeholder focus:border-border-focus focus:outline-none";

export function Selo({ estado }: { estado: string }) {
  const cores: Record<string, string> = {
    active: "text-success border-success/40",
    past_due: "text-warning border-warning/40",
    canceled: "text-text-muted border-border",
  };
  const texto: Record<string, string> = {
    active: "ativa",
    past_due: "em atraso",
    canceled: "cancelada",
  };
  return (
    <span
      className={cn(
        "rounded-full border px-2 py-0.5 text-xs",
        cores[estado] ?? "text-text-muted border-border",
      )}
    >
      {texto[estado] ?? estado}
    </span>
  );
}

export function Cartao({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("rounded-xl border border-border bg-bg-secondary p-5", className)}>
      {children}
    </div>
  );
}

export function Vazio({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-border px-5 py-10 text-center text-sm text-text-muted">
      {children}
    </div>
  );
}

// "há 3 h" em vez de um carimbo ISO. Quem opera quer saber se a instalação falou com o hub hoje, não
// em que fuso ela estava.
export function quandoFoi(iso: string | null): string {
  if (!iso) return "nunca";
  const seg = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seg < 60) return "agora";
  if (seg < 3600) return `há ${Math.floor(seg / 60)} min`;
  if (seg < 86_400) return `há ${Math.floor(seg / 3600)} h`;
  return `há ${Math.floor(seg / 86_400)} d`;
}

// Uma janela sobre a tela. Pequena de proposito: o painel tem UM uso para ela (falar com o suporte),
// e uma biblioteca de dialogos antes do segundo uso e abstracao sem demanda.
//
// 🔴 Esc fecha e o fundo fecha. Sem isso, quem abre sem querer num celular fica preso: nao ha tecla
// visivel, e o unico caminho de volta seria recarregar a pagina.
export function Janela({
  titulo,
  aoFechar,
  children,
}: {
  titulo: string;
  aoFechar: () => void;
  children: ReactNode;
}) {
  useEffect(() => {
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") aoFechar();
    };
    document.addEventListener("keydown", aoTeclar);
    return () => document.removeEventListener("keydown", aoTeclar);
  }, [aoFechar]);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: o fundo e um atalho; Esc e o fechar sao o caminho acessivel.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={aoFechar}
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: so impede que o clique dentro feche a janela. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={titulo}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border border-border bg-bg-secondary shadow-xl"
      >
        <div className="flex items-center justify-between gap-3 border-border border-b px-5 py-4">
          <h2 className="font-semibold text-lg text-text-primary">{titulo}</h2>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="rounded-lg p-1 text-text-muted transition-colors hover:bg-bg-tertiary hover:text-text-primary"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="flex flex-col gap-4 px-5 py-5">{children}</div>
      </div>
    </div>
  );
}
